import {
  ColumnDef,
  ColumnFiltersState,
  PaginationState,
  RowPinningState,
  RowSelectionState,
  SortingState,
  Updater,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { ActionCell } from "./cells/actionCell";
import ExpansionCell from "./cells/expansionCell";
import SelectionCell from "./cells/selectionCell";
import ExpansionHeader from "./headers/expansionHeader";
import SelectionHeader from "./headers/selectionHeader";
import { RowQuery, RowQueryRequest, RowQueryResult, TableProps } from "./table";
import {
  canMultiSelectColumn,
  canSelectColumn,
  isRemote,
  parseFromRowSelection,
  parseToRowSelection,
} from "./utils";

export function useTable<RowType>(props: TableProps<RowType>) {
  const {
    data,
    onQueryRequest,
    pageCount,
    classNames,
    bordered = true,
    striped = true,
    size = "lg",
    enablePaging = false,
    defaultPageSize = 5,
    enableSorting = false,
    defaultSorting = [],
    enableRowSelection = false,
    enableMultiRowSelection = false,
    enableSubRowSelection = false,
    defaultRowSelection = [],
    rowSelection: userRowSelection,
    enableExpanding = false,
    enableColumnSelection = false,
    enableMultiColumnSelection = false,
    defaultColumnSelection = [],
    columnSelection: userColumnSelection,
    enableFilters = false,
    defaultFilters = [],
    getSubRows,
    getRowId,
    getRowCanExpand,
    onRowSelection,
    onColumnSelection,
    enableChipAdd = true,
    onChipAdd,
    onChipSelected,
    actions,
    onActionClicked,
    enableRowAction,
    enableRowActions,
    enableRowPinning,
    defaultPinning,
    pageResetKey,
  } = props;

  const controlledRemote = isRemote(data);
  const remote = controlledRemote || !!onQueryRequest;
  const initialData = !remote && Array.isArray(data) ? data : [];
  const manualPaging = remote && enablePaging;
  const manualSorting = remote && enableSorting;
  const manualFiltering = remote && enableFilters;

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: defaultPageSize,
  });
  const [rowPinning, setRowPinning] = useState<RowPinningState>(
    defaultPinning ?? {
      top: [],
      bottom: [],
    },
  );

  const [sorting, setSorting] = useState<SortingState>(defaultSorting);
  const [filters, setFilters] = useState<ColumnFiltersState>(defaultFilters);
  useEffect(() => {
    setFilters(defaultFilters);
  }, [JSON.stringify(defaultFilters)]);

  const [tableData, setTableData] = useState<RowQueryResult<RowType>>({
    data: initialData,
    pageCount: -1,
  });

  const [focusedColumn, setFocusedColumn] = useState<string>();
  const [selectedColumns, setSelectedColumns] = useState<string[]>(
    defaultColumnSelection,
  );

  const tableColumns = useTableColumns(props);

  const remoteEffect = controlledRemote ? useRemoteQuery : () => {};
  remoteEffect(
    sorting,
    filters,
    pagination.pageSize,
    pagination.pageIndex,
    data as RowQuery<RowType>,
    setTableData,
  );

  useOnQueryChange(
    sorting,
    filters,
    pagination.pageSize,
    pagination.pageIndex,
    onQueryRequest,
  );

  useResetPageOnFilterChange(remote, filters, setPagination);

  useEffect(() => {
    if (pageResetKey === undefined) return;
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, [pageResetKey]);

  useEffect(() => {
    if (!!onColumnSelection) onColumnSelection(selectedColumns);
  }, [onColumnSelection, selectedColumns]);

  const onColumnFocus = useColumnFocus(
    enableColumnSelection,
    enableMultiColumnSelection,
    setFocusedColumn,
  );
  const onColumnSelected = useColumnSelection(
    enableColumnSelection,
    enableMultiColumnSelection,
    setSelectedColumns,
  );

  const [selectedRows, setSelectedRows] = useRowSelection(
    defaultRowSelection,
    userRowSelection,
    onRowSelection,
  );

  return useReactTable<RowType>({
    columns: tableColumns,
    data: controlledRemote ? tableData.data : (data as RowType[]),
    pageCount: pageCount
      ? pageCount
      : manualPaging
        ? tableData.pageCount
        : undefined,
    manualPagination: manualPaging,
    manualSorting: manualSorting,
    state: {
      pagination: pagination,
      sorting: sorting,
      columnFilters: filters,
      rowSelection: selectedRows,
      rowPinning: rowPinning,
    },
    enableRowSelection: enableRowSelection,
    enableSorting: enableSorting,
    enableExpanding: enableExpanding,
    paginateExpandedRows: !enablePaging,
    enableMultiRowSelection: enableMultiRowSelection,
    enableSubRowSelection: enableSubRowSelection,
    enableFilters: enableFilters,
    manualFiltering: manualFiltering,
    enableRowPinning: enableRowPinning,
    defaultColumn: {
      filterFn: "includesString",
    },
    meta: {
      focusedColumn: focusedColumn,
      selectedColumns: userColumnSelection || selectedColumns,
      onColumnFocus: onColumnFocus,
      onColumnSelected: onColumnSelected,
      enableChipAdd: enableChipAdd,
      onChipAdd: onChipAdd,
      onChipSelected: onChipSelected,
      actions: actions,
      onActionClicked: onActionClicked,
      enableRowAction: enableRowAction,
      enableRowActions: enableRowActions,
      bordered: bordered,
      striped: striped,
      size: size,
      classNames: classNames,
    },
    onRowPinningChange: setRowPinning,
    onColumnFiltersChange: setFilters,
    getRowCanExpand: getRowCanExpand,
    getSubRows: getSubRows,
    getRowId: getRowId,
    autoResetPageIndex: false,
    onPaginationChange: setPagination,
    onRowSelectionChange: setSelectedRows,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel:
      !manualPaging && enablePaging ? getPaginationRowModel() : undefined,
    getSortedRowModel:
      !manualSorting && enablePaging ? getSortedRowModel() : undefined,
    getExpandedRowModel: enableExpanding ? getExpandedRowModel() : undefined,
    getFilteredRowModel:
      !controlledRemote && enableFilters ? getFilteredRowModel() : undefined,
  });
}

export function useTableColumns<RowType>(props: TableProps<RowType>) {
  const { columns, enableMultiRowSelection, enableExpanding, actions } = props;

  const { t } = useTranslation();

  return useMemo(() => {
    let startColumns: ColumnDef<RowType>[] = [];
    let endColumns: ColumnDef<RowType>[] = [];

    if (enableExpanding) {
      startColumns = [
        {
          id: "expansion",
          header: ExpansionHeader,
          cell: ExpansionCell,
        },
        ...startColumns,
      ];
    }

    if (!!enableMultiRowSelection) {
      startColumns = [
        {
          id: "selection",
          header: SelectionHeader,
          cell: SelectionCell,
        },
        ...startColumns,
      ];
    }

    if (!!actions && !!actions.length) {
      endColumns = [
        {
          header: t("common.label.actions", "Actions") as string,
          cell: ActionCell,
        },
        ...endColumns,
      ];
    }

    columns.forEach((column) => {
      if ("cell" in column && !column.cell) {
        delete column.cell;
      }
    });

    if (!startColumns.length && !endColumns.length) {
      return columns;
    }

    if (columns.some((column) => "columns" in column)) {
      let result: ColumnDef<RowType>[] = [];

      if (!!startColumns.length) {
        result.push({
          id: "start",
          columns: startColumns,
        });
      }

      result.push(...columns);

      if (!!endColumns.length) {
        result.push({
          id: "end",
          columns: endColumns,
        });
      }

      return result;
    }

    return [...startColumns, ...columns, ...endColumns];
  }, [columns, enableMultiRowSelection, enableExpanding, actions, t]);
}

export function useOnQueryChange(
  sorting: SortingState,
  filters: ColumnFiltersState,
  pageSize: number,
  pageIndex: number,
  onQueryChange: ((request: RowQueryRequest) => void) | undefined,
) {
  useEffect(() => {
    if (!onQueryChange) return;

    onQueryChange({
      page: pageIndex,
      pageSize: pageSize,
      sorting: sorting,
      filters: filters,
    });
  }, [sorting, JSON.stringify(filters), onQueryChange, pageSize, pageIndex]);
}

export function useRemoteQuery<RowType>(
  sorting: SortingState,
  filters: ColumnFiltersState,
  pageSize: number,
  pageIndex: number,
  query: RowQuery<RowType>,
  onComplete: (result: RowQueryResult<RowType>) => void,
) {
  const [tokens, setTokens] = useState<Record<number, string | undefined>>({});

  useEffect(() => {
    async function load() {
      const result = await query(
        {
          page: pageIndex,
          pageSize: pageSize,
          sorting: sorting,
          filters: filters,
        },
        tokens,
      );

      onComplete({
        data: result.data,
        pageCount: result.pageCount,
      });

      setTokens((prev) => ({ ...prev, [pageIndex + 1]: result.nextPageToken }));
    }

    load();
  }, [
    sorting,
    JSON.stringify(filters),
    query,
    onComplete,
    pageSize,
    pageIndex,
  ]);
}

export function useResetPageOnFilterChange(
  remote: boolean,
  filters: ColumnFiltersState,
  setPagination: (value: SetStateAction<PaginationState>) => void,
) {
  useEffect(() => {
    if (remote) {
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    }
  }, [remote, JSON.stringify(filters), setPagination]);
}

export function useColumnFocus<RowType>(
  enableColumnSelection: boolean | ((column: ColumnDef<RowType>) => boolean),
  enableMultiColumnSelection:
    | boolean
    | ((column: ColumnDef<RowType>) => boolean),
  setFocusedColumn: (value: SetStateAction<string | undefined>) => void,
) {
  return useCallback(
    (column: ColumnDef<RowType>, entered: boolean) => {
      const id = column.id;
      if (!id) return;

      if (
        canMultiSelectColumn(column, enableMultiColumnSelection) ||
        canSelectColumn(column, enableColumnSelection)
      )
        setFocusedColumn(entered ? id : undefined);
    },
    [enableColumnSelection, enableMultiColumnSelection, setFocusedColumn],
  );
}

export function useColumnSelection<RowType>(
  enableColumnSelection: boolean | ((column: ColumnDef<RowType>) => boolean),
  enableMultiColumnSelection:
    | boolean
    | ((column: ColumnDef<RowType>) => boolean),
  setSelectedColumns: (value: SetStateAction<string[]>) => void,
) {
  return useCallback(
    (column: ColumnDef<RowType>) => {
      const id = column.id;
      if (!id) return;

      if (canMultiSelectColumn(column, enableMultiColumnSelection)) {
        setSelectedColumns((state) => {
          if (state.includes(id)) return [...state.filter((x) => x !== id)];
          return [...state, id];
        });
      } else if (canSelectColumn(column, enableColumnSelection)) {
        setSelectedColumns((state) => {
          if (state.includes(id)) return [];
          return [id];
        });
      }
    },
    [enableColumnSelection, enableMultiColumnSelection, setSelectedColumns],
  );
}

export function useRowSelection(
  defaultSelection: string[],
  currentRowSelection?: string[],
  onSelect?: (rows: string[]) => void,
) {
  const [selected, setSelected] = useState(
    parseToRowSelection(defaultSelection),
  );

  const onSetSelected = useCallback(
    (e: Updater<RowSelectionState>) => {
      const state = currentRowSelection
        ? parseToRowSelection(currentRowSelection)
        : selected;

      const next = typeof e === "function" ? e(state) : e;
      setSelected(next);
      onSelect?.(parseFromRowSelection(next));
    },
    [selected, currentRowSelection, onSelect],
  );

  return [
    currentRowSelection ? parseToRowSelection(currentRowSelection) : selected,
    onSetSelected,
  ] as const;
}
